import { useState, useEffect, useContext } from "react";
import AppShell, { ThemeCtx, useLogoColors, buildTheme } from "./AppShell";
import { post, C, LOGO_BASE, THUMB_BASE, ATTACH_BASE } from "./utils";

const API = "https://cip.medsphere.net/api";

const isImg    = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const thumbUrl = (p="") => THUMB_BASE + p.split("/").pop();
const attachUrl= (p="") => ATTACH_BASE + p.split("/").pop();
const openDoc  = (doc, subId) => {
  const base = window.location.origin + window.location.pathname;
  window.open(`${base}?doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId}`,
    `pvdoc_${doc.DOCUMENTID}`, "noopener,noreferrer");
};

function Spin() {
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
      height:"200px",gap:"10px",color:C.muted,fontSize:"14px"}}>
      <div style={{width:"18px",height:"18px",border:`2px solid ${C.border}`,
        borderTopColor:C.primary,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
      Loading…
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ── Share Modal ───────────────────────────────────────────────────────────────
function ShareModal({ doc, user, activeSub, tPrimary, onClose }) {
  const [tab,      setTab]      = useState("users");
  // users tab
  const [users,    setUsers]    = useState([]);
  const [uLoading, setULoading] = useState(true);
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState([]);
  const [sending,  setSending]  = useState(false);
  const [result,   setResult]   = useState(null);
  // email tab
  const [input,    setInput]    = useState("");
  const [emails,   setEmails]   = useState([]);

  useEffect(()=>{
    const fd = new FormData();
    fd.append("userid",       user.USERID);
    fd.append("subprojectid", activeSub?.SUBPROJECTID);
    fetch(`${API}/DM_Users`,{method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd})
      .then(r=>r.json())
      .then(res=>{ if(Array.isArray(res.DATA)) setUsers(res.DATA); })
      .catch(()=>{}).finally(()=>setULoading(false));
  },[]);

  const filtered = users.filter(u=>
    (u.NAME||"").toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (uid) => setSelected(prev=>
    prev.includes(uid)?prev.filter(x=>x!==uid):[...prev,uid]);

  const addEmail = (raw) => {
    const vals = raw.split(/[,\s]+/).map(e=>e.trim().toLowerCase()).filter(e=>e.includes("@"));
    if(!vals.length) return;
    setEmails(prev=>[...new Set([...prev,...vals])]); setInput("");
  };

  const send = async () => {
    if(tab==="users"&&!selected.length) return;
    if(tab==="email"&&!emails.length) return;
    setSending(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("documentid",   doc.DOCUMENTID);
      fd.append("userid",       user.USERID);
      fd.append("subprojectid", activeSub?.SUBPROJECTID);
      if(tab==="users") selected.forEach(uid=>fd.append("receiver[]", uid));
      if(tab==="email") emails.forEach(e=>fd.append("email[]", e));
      const r = await fetch(`${API}/share_newsletter`,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const res = await r.json();
      if(res.STATUS==="1"||res.STATUS===1){
        const count = tab==="users"?selected.length:emails.length;
        setResult({ok:true,msg:`Shared with ${count} recipient${count!==1?"s":""}`});
        setSelected([]); setEmails([]); setInput("");
      } else setResult({ok:false,msg:res.MESSAGE||"Share failed."});
    } catch { setResult({ok:false,msg:"Network error."}); }
    setSending(false);
  };

  const initials = (u) => {
    const parts = (u.NAME||"").trim().split(" ");
    return ((parts[0]||"")[0]||(parts[1]||"")[0]||"?").toUpperCase() +
           ((parts[1]||"")[0]||"").toUpperCase();
  };
  const canSend = tab==="users"?selected.length>0:emails.length>0;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:9000,
      display:"flex",alignItems:"center",justifyContent:"center"}}
      onClick={onClose}>
      <div style={{background:"#fff",borderRadius:16,width:"min(480px,94vw)",
        maxHeight:"80vh",display:"flex",flexDirection:"column",
        boxShadow:"0 20px 60px rgba(0,0,0,0.2)",overflow:"hidden"}}
        onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{padding:"16px 20px",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontSize:16,fontWeight:700,color:"#111"}}>Share NewsFlash</div>
            <button onClick={onClose} style={{background:"none",border:"none",
              cursor:"pointer",fontSize:22,color:"#9CA3AF",lineHeight:1,padding:0}}>×</button>
          </div>
          <div style={{fontSize:12,color:"#6B7280",marginTop:2}}>{doc.SUBJECT}</div>
        </div>
        {/* Tabs */}
        <div style={{display:"flex",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>
          {[
            {id:"users",label:"👤 Users"},
            {id:"email",label:"📧 Email"},
          ].map(t=>(
            <button key={t.id} onClick={()=>{setTab(t.id);setResult(null);}}
              style={{flex:1,padding:"11px",border:"none",background:"none",cursor:"pointer",
                fontSize:13,fontWeight:tab===t.id?700:400,
                color:tab===t.id?tPrimary:"#6B7280",
                borderBottom:`2px solid ${tab===t.id?tPrimary:"transparent"}`}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Users tab */}
        {tab==="users"&&(
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Search + Select All */}
            <div style={{padding:"12px 16px",borderBottom:"1px solid #F3F4F6",flexShrink:0,
              display:"flex",alignItems:"center",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flex:1,
                background:"#F9FAFB",borderRadius:8,padding:"7px 12px",
                border:"1px solid #E5E7EB"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input value={search} onChange={e=>setSearch(e.target.value)}
                  placeholder="Search users..."
                  style={{border:"none",outline:"none",background:"transparent",
                    fontSize:13,flex:1,color:"#111"}}/>
              </div>
              {/* Select All */}
              <div onClick={()=>{
                  const allIds = filtered.map(u=>u.USERID);
                  const allSelected = allIds.every(id=>selected.includes(id));
                  setSelected(allSelected ? selected.filter(id=>!allIds.includes(id)) : [...new Set([...selected,...allIds])]);
                }}
                style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer",
                  flexShrink:0,fontSize:12,color:C.muted,userSelect:"none"}}>
                <div style={{width:18,height:18,borderRadius:4,flexShrink:0,
                  border:`2px solid ${filtered.length>0&&filtered.every(u=>selected.includes(u.USERID))?tPrimary:"#D1D5DB"}`,
                  background:filtered.length>0&&filtered.every(u=>selected.includes(u.USERID))?tPrimary:"#fff",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {filtered.length>0&&filtered.every(u=>selected.includes(u.USERID))&&(
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8"
                        strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                All
              </div>
            </div>
            {/* User list */}
            <div style={{flex:1,overflowY:"auto"}}>
              {uLoading&&<div style={{textAlign:"center",padding:"24px",color:"#9CA3AF",fontSize:13}}>Loading…</div>}
              {!uLoading&&filtered.map(u=>{
                const uid = u.USERID;
                const isSel = selected.includes(uid);
                return (
                  <div key={uid} onClick={()=>toggle(uid)}
                    style={{display:"flex",alignItems:"center",gap:12,
                      padding:"10px 16px",cursor:"pointer",
                      background:isSel?"#F0F9FF":"transparent",
                      borderBottom:"1px solid #F9FAFB",transition:"background .1s"}}
                    onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.background="#F9FAFB"; }}
                    onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.background="transparent"; }}>
                    {/* Avatar */}
                    <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                      background:`${tPrimary}20`,color:tPrimary,fontSize:12,fontWeight:700,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {initials(u)}
                    </div>
                    <div style={{flex:1,fontSize:13,color:"#111",fontWeight:500}}>
                      {u.NAME}
                    </div>
                    {/* Checkbox */}
                    <div style={{width:18,height:18,borderRadius:4,flexShrink:0,
                      border:`2px solid ${isSel?tPrimary:"#D1D5DB"}`,
                      background:isSel?tPrimary:"#fff",
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {isSel&&<svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Email tab */}
        {tab==="email"&&(
          <div style={{padding:"16px",flex:1}}>
            <div style={{fontSize:12,color:"#6B7280",marginBottom:8}}>
              Enter email addresses — press Enter or comma to add
            </div>
            <div style={{border:"1px solid #E5E7EB",borderRadius:8,padding:"8px 10px",
              display:"flex",flexWrap:"wrap",gap:4,minHeight:48,cursor:"text",marginBottom:8}}
              onClick={()=>document.getElementById("nfEmail")?.focus()}>
              {emails.map(e=>(
                <span key={e} style={{background:`${tPrimary}15`,color:tPrimary,
                  fontSize:12,padding:"2px 8px",borderRadius:20,display:"flex",
                  alignItems:"center",gap:4}}>
                  {e}
                  <span onClick={ev=>{ev.stopPropagation();setEmails(prev=>prev.filter(x=>x!==e));}}
                    style={{cursor:"pointer",opacity:0.6,fontSize:14}}>×</span>
                </span>
              ))}
              <input id="nfEmail" value={input}
                onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter"||e.key===","){e.preventDefault();addEmail(input);}}}
                onBlur={()=>input&&addEmail(input)}
                placeholder={emails.length?"":"name@company.com"}
                style={{border:"none",outline:"none",fontSize:13,flex:1,
                  minWidth:140,background:"transparent"}}/>
            </div>
          </div>
        )}

        {/* Result */}
        {result&&(
          <div style={{padding:"0 16px 8px",fontSize:12,
            color:result.ok?"#16A34A":"#DC2626"}}>{result.msg}</div>
        )}

        {/* Footer */}
        <div style={{padding:"12px 16px",borderTop:"1px solid #E5E7EB",
          display:"flex",justifyContent:"flex-end",gap:8,flexShrink:0}}>
          <button onClick={onClose}
            style={{padding:"8px 20px",borderRadius:8,border:"1px solid #E5E7EB",
              background:"#fff",color:"#6B7280",fontSize:13,cursor:"pointer"}}>
            Cancel
          </button>
          <button onClick={send} disabled={sending||!canSend}
            style={{padding:"8px 24px",borderRadius:8,border:"none",
              background:tPrimary,color:"#fff",fontSize:13,fontWeight:600,
              cursor:sending||!canSend?"not-allowed":"pointer",
              opacity:sending||!canSend?0.6:1}}>
            {sending?"Sharing…":"Share"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewsFlashView({ user, activeSub, docId, stateid="2" }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const ctx      = useContext(ThemeCtx)||{};
  const tPrimary = ctx.theme?.primary || C.primary;

  useEffect(()=>{
    if(!user?.USERID || !activeSub?.SUBPROJECTID || !docId) return;
    post("newsletter", {
      subprojectid: activeSub.SUBPROJECTID,
      userid:       user.USERID,
      documentid:   docId,
      stateid:      Number(stateid),
    }, user.AUTH_CODE)
    .then(r=>{
      if(r.STATUS==="1"||r.STATUS===1){
        const doc = Array.isArray(r.DATA)?r.DATA[0]:(r.DATA||null);
        if(doc){
          // RELATIVE and LOGO live at root level, not inside DATA[0]
          doc._RELATIVE = Array.isArray(r.RELATIVE)?r.RELATIVE:[];
          doc._LOGO     = r.LOGO||null;
        }
        setData(doc);
      } else setError(r.MESSAGE||"Not found");
    })
    .catch(()=>setError("Failed to load"))
    .finally(()=>setLoading(false));
  },[docId, activeSub?.SUBPROJECTID]);

  if(loading) return <Spin/>;
  if(error||!data) return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",
      height:"100%",color:"#EF4444",fontSize:"13px"}}>{error||"Not found"}</div>
  );

  const relative = data._RELATIVE || [];
  const chatbot  = Array.isArray(data.CHATBOT)?data.CHATBOT:[];
  const images   = chatbot.filter(a=>isImg(a.FILEPATH));
  const files    = chatbot.filter(a=>!isImg(a.FILEPATH));
  const logoUrl  = data._LOGO||null;

  return (
    <div>
      {shareOpen&&<ShareModal doc={data} user={user} activeSub={activeSub}
        tPrimary={tPrimary} onClose={()=>setShareOpen(false)}/>}
      {/* Hero */}
      <div style={{flexShrink:0,padding:"20px 24px",
        background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}88)`,
        position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",right:"-30px",top:"-30px",width:"160px",height:"160px",
          borderRadius:"50%",background:"rgba(255,255,255,0.05)"}}/>
        {/* Share button */}
        <button onClick={()=>setShareOpen(true)}
          style={{position:"absolute",top:14,right:14,zIndex:10,
            display:"flex",alignItems:"center",gap:5,
            padding:"5px 12px",borderRadius:20,
            background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.35)",
            color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer"}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/>
            <circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share
        </button>
        <div style={{display:"flex",gap:"14px",alignItems:"flex-start",position:"relative"}}>
          {logoUrl&&(
            <div style={{width:"48px",height:"48px",borderRadius:"10px",flexShrink:0,
              background:"rgba(255,255,255,0.18)",border:"1.5px solid rgba(255,255,255,0.3)",
              overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
                style={{width:"80%",height:"80%",objectFit:"contain"}}/>
            </div>
          )}
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"inline-flex",background:"rgba(255,255,255,0.2)",
              borderRadius:"20px",padding:"2px 10px",marginBottom:"6px",fontSize:"9px",
              fontWeight:"700",color:"rgba(255,255,255,0.9)",textTransform:"uppercase",
              letterSpacing:"0.6px"}}>📰 NewsFlash</div>
            <div style={{fontSize:"20px",fontWeight:"800",color:"#fff",lineHeight:1.25,
              marginBottom:"6px"}}>{data.SUBJECT}</div>
            <div style={{display:"flex",gap:"8px",flexWrap:"wrap",alignItems:"center"}}>
              {data.DISPLAY_TIME&&(
                <span style={{fontSize:"11px",color:"rgba(255,255,255,0.8)",
                  background:"rgba(255,255,255,0.15)",borderRadius:"5px",padding:"2px 8px"}}>
                  📅 {data.DISPLAY_TIME}
                </span>
              )}
              {data.FIRSTNAME&&(
                <span style={{fontSize:"11px",color:"rgba(255,255,255,0.8)",fontWeight:"600"}}>
                  {data.FIRSTNAME}
                </span>
              )}
            </div>
          </div>
          {images.length>0&&(
            <div style={{width:"64px",height:"64px",borderRadius:"10px",flexShrink:0,
              overflow:"hidden",border:"2px solid rgba(255,255,255,0.3)"}}>
              <img src={thumbUrl(images[0].FILEPATH)} alt=""
                onError={e=>e.target.style.display="none"}
                style={{width:"100%",height:"100%",objectFit:"contain"}}/>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{padding:"20px 24px 40px"}}>

        {/* Notes */}
        {data.NOTES&&(
          <div style={{background:"#fff",borderRadius:"12px",marginBottom:"16px",
            border:"1px solid #E5E7EB",padding:"16px 18px",
            boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
            fontSize:"13px",color:"#374151",lineHeight:1.8}}
            dangerouslySetInnerHTML={{__html:data.NOTES}}/>
        )}

        {/* Attachments */}
        {files.length>0&&(
          <div style={{background:"#fff",borderRadius:"12px",marginBottom:"16px",
            border:"1px solid #E5E7EB",padding:"12px 16px",
            boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{fontSize:"11px",fontWeight:"700",color:"#374151",
              textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:"10px"}}>
              Attachments
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>
              {files.map((f,i)=>(
                <a key={i} href={attachUrl(f.FILEPATH)} target="_blank" rel="noopener noreferrer"
                  style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",
                    borderRadius:8,border:`1px solid ${C.border}`,background:"#F8FAFC",
                    color:C.primary,fontSize:12,textDecoration:"none",fontWeight:500}}>
                  📎 {f.FILEPATH.split("/").pop()}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Related documents */}
        {relative.length>0&&(
          <div style={{background:"#fff",borderRadius:"12px",marginBottom:"16px",
            border:"1px solid #E5E7EB",overflow:"hidden",
            boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
            <div style={{padding:"10px 16px",background:"#F8FAFC",
              borderBottom:"1px solid #F1F5F9",fontSize:"11px",fontWeight:"700",
              color:"#374151",textTransform:"uppercase",letterSpacing:"0.5px",
              position:"sticky",top:0,zIndex:1}}>
              Related ({relative.length})
            </div>
            <div>
            {relative.map((r,i)=>{
              const rLogo  = r.COMPANY_LOGO ? LOGO_BASE+r.COMPANY_LOGO : null;
              const rThumb = Array.isArray(r.CHATBOT)?r.CHATBOT.find(f=>isImg(f.FILEPATH)):null;
              return (
                <div key={i} onClick={()=>openDoc(r, activeSub?.SUBPROJECTID)}
                  style={{display:"flex",gap:"12px",padding:"12px 16px",cursor:"pointer",
                    borderTop:i>0?"1px solid #F1F5F9":"none",
                    transition:"background .12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  {/* Logo */}
                  <div style={{width:"40px",height:"40px",borderRadius:"8px",flexShrink:0,
                    background:"#F3F4F6",border:`1px solid ${C.border}`,overflow:"hidden",
                    display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {rLogo
                      ?<img src={rLogo} alt="" onError={e=>e.target.style.display="none"}
                          style={{width:"90%",height:"90%",objectFit:"contain"}}/>
                      :<span style={{fontSize:14,color:C.muted}}>📄</span>}
                  </div>
                  {/* Content */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:"13px",fontWeight:"600",color:C.text,
                      lineHeight:1.35,marginBottom:3,
                      display:"-webkit-box",WebkitLineClamp:2,
                      WebkitBoxOrient:"vertical",overflow:"hidden"}}>
                      {r.BRANDNAME&&r.BRANDID!==0&&(
                        <span style={{color:tPrimary}}>{r.BRANDNAME}: </span>
                      )}
                      {r.TITLE||r.SUBJECT}
                    </div>
                    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",alignItems:"center",marginBottom:3}}>
                      {r.INDICATIONNAME&&r.INDICATIONID!==0&&(
                        <span style={{fontSize:"10px",color:C.muted}}>{r.INDICATIONNAME}</span>
                      )}
                      {r.DOCTYPENAME&&(
                        <span style={{fontSize:"10px",fontWeight:"600",
                          padding:"1px 6px",borderRadius:"4px",
                          background:`${tPrimary}15`,color:tPrimary}}>
                          {r.DOCTYPENAME}
                        </span>
                      )}
                      {r.DOCDATE&&(
                        <span style={{fontSize:"10px",color:C.hint}}>· {r.DOCDATE}</span>
                      )}
                    </div>
                    {r.MESSAGES&&(
                      <div style={{fontSize:"11px",color:C.muted,lineHeight:1.45,
                        display:"-webkit-box",WebkitLineClamp:2,
                        WebkitBoxOrient:"vertical",overflow:"hidden"}}
                        dangerouslySetInnerHTML={{__html:r.MESSAGES}}/>
                    )}
                  </div>
                  {/* Thumb */}
                  {rThumb&&(
                    <div style={{width:"52px",height:"52px",borderRadius:"8px",flexShrink:0,
                      overflow:"hidden",border:`1px solid ${C.border}`,background:"#F3F4F6"}}>
                      <img src={thumbUrl(rThumb.FILEPATH)} alt=""
                        onError={e=>e.target.style.display="none"}
                        style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                    </div>
                  )}
                </div>
              );
            })}
            </div>{/* end scroll wrapper */}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NewsFlashDetailPage({ session, onLogout, docId, pid }) {
  const user       = session?.user;
  const subs       = user?.SUBPROJECTS || [];
  const p          = new URLSearchParams(window.location.search);
  const urlSub     = p.get("sub");
  const activeSub  = urlSub
    ? (subs.find(s=>String(s.SUBPROJECTID)===urlSub) || session?.subproject || subs[0])
    : (session?.subproject || subs[0]);

  return (
    <AppShell session={{...session, subproject:activeSub}}
      onLogout={onLogout}
      hideNav={true}
      hideFilterButtons={true}
      allowNavOverride={false}
      overrideContent={
        <NewsFlashView
          user={user}
          activeSub={activeSub}
          docId={docId}
          stateid="2"/>
      }/>
  );
}